import { useEffect, useState } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

export type AudienceRemoveMode = 'delete' | 'archive';

export default function AudienceDeleteDialog({
  open,
  onOpenChange,
  audienceName,
  mode,
  dependentCampaignCount = 0,
  isLoadingDependencies = false,
  isSubmitting = false,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  audienceName: string;
  mode: AudienceRemoveMode;
  dependentCampaignCount?: number;
  isLoadingDependencies?: boolean;
  isSubmitting?: boolean;
  onConfirm: () => void;
}) {
  const { LL } = useI18nContext();
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) {
      setAcknowledged(false);
    }
  }, [open]);

  const handleConfirm = (event: React.MouseEvent) => {
    event.preventDefault();
    onConfirm();
  };

  const isArchive = mode === 'archive';
  const confirmDisabled =
    isSubmitting || isLoadingDependencies || (isArchive && !acknowledged);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isArchive
              ? LL.audiences.details.actions.archiveConfirmTitle()
              : LL.audiences.details.actions.deleteConfirmTitle()}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              {isLoadingDependencies ? (
                <p>{LL.audiences.details.actions.checkingDependencies()}</p>
              ) : isArchive ? (
                <p>
                  {LL.audiences.details.actions.archiveConfirmDescription({
                    audienceName,
                    count: dependentCampaignCount,
                  })}
                </p>
              ) : (
                <p>
                  {LL.audiences.details.actions.deleteConfirmDescription({
                    audienceName,
                  })}
                </p>
              )}

              {isArchive && !isLoadingDependencies ? (
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="scheduled-campaigns-acknowledgment"
                    checked={acknowledged}
                    onCheckedChange={(checked) =>
                      setAcknowledged(checked === true)
                    }
                    disabled={isSubmitting}
                  />
                  <Label
                    htmlFor="scheduled-campaigns-acknowledgment"
                    className="text-sm font-normal leading-snug text-muted-foreground"
                  >
                    {LL.audiences.details.actions.scheduledCampaignsAcknowledgment()}
                  </Label>
                </div>
              ) : null}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>
            {LL.campaigns.goBackButton()}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isSubmitting
              ? isArchive
                ? LL.audiences.details.actions.archiving()
                : LL.audiences.details.actions.deleting()
              : isArchive
                ? LL.audiences.details.actions.archiveAudience()
                : LL.audiences.details.actions.deleteAudience()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
